import React, { useMemo, useState } from "react";
import { FaTooth } from "react-icons/fa";
import styles from "./Results.module.css";

function getTextColorForBackground(backgroundColor) {
  if (!backgroundColor) return "#ffffff";
  
  let r, g, b;
  
  if (backgroundColor.startsWith("#")) {
    const hex = backgroundColor.replace("#", "");
    if (hex.length === 3) {
      r = parseInt(hex[0] + hex[0], 16);
      g = parseInt(hex[1] + hex[1], 16);
      b = parseInt(hex[2] + hex[2], 16);
    } else {
      r = parseInt(hex.substr(0, 2), 16);
      g = parseInt(hex.substr(2, 2), 16);
      b = parseInt(hex.substr(4, 2), 16);
    }
  } else if (backgroundColor.startsWith("rgb")) {
    const matches = backgroundColor.match(/\d+/g);
    if (matches && matches.length >= 3) {
      r = parseInt(matches[0]);
      g = parseInt(matches[1]);
      b = parseInt(matches[2]);
    } else {
      return "#ffffff";
    }
  } else {
    return "#ffffff";
  }
  
  if (isNaN(r) || isNaN(g) || isNaN(b)) {
    return "#ffffff";
  }
  
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  
  return luminance > 0.5 ? "#1a1a1a" : "#ffffff";
}

function Results({
  items = [],
  collapsed = false,
  onToggleBox,
  onShowAll,
  onHideAll,
  onExport,
  imageName,
  onClose,
  displayPreferences = {},
  onDisplayPreferencesChange,
  onHoverItem,
}) {
  const [classFilter, setClassFilter] = useState("all");
  const [sortBy, setSortBy] = useState("none");
  
  const {
    showConfidence = true,
    showBorder = true,
    showFill = true,
  } = displayPreferences;

  const allVisible = items.length > 0 && items.every((item) => !item.hidden);
  const allHidden = items.length > 0 && items.every((item) => item.hidden);

  const classOptions = useMemo(
    () => ["all", ...Array.from(new Set(items.map((i) => i.label)))],
    [items]
  );

  const filteredItems = useMemo(() => {
    let filtered = items.filter((item) => {
      const passesClass = classFilter === "all" || item.label === classFilter;
      return passesClass;
    });

    if (sortBy === "type") {
      filtered = [...filtered].sort((a, b) => {
        const labelA = a.label || "";
        const labelB = b.label || "";
        return labelA.localeCompare(labelB);
      });
    } else if (sortBy === "confidence") {
      filtered = [...filtered].sort((a, b) => {
        const scoreA = a.score ?? 0;
        const scoreB = b.score ?? 0;
        return scoreB - scoreA;
      });
    }

    return filtered;
  }, [items, classFilter, sortBy]);

  return (
    <aside
      className={`${styles.results} ${collapsed ? styles.collapsed : ""}`}
      aria-hidden={collapsed}
    >
      <div className={styles.header}>
        <span>Results</span>
        <span className={styles.count}>{filteredItems.length}</span>
        <div style={{ flex: 1 }}></div>
        {onClose && (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close results"
          >
            ×
          </button>
        )}
      </div>
      {onDisplayPreferencesChange && (
        <div className={styles.customization}>
          <div className={styles.customizationTitle}>Display Options</div>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showConfidence}
              onChange={(e) =>
                onDisplayPreferencesChange({
                  ...displayPreferences,
                  showConfidence: e.target.checked,
                })
              }
            />
            <span>Show labels and confidence</span>
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showBorder}
              onChange={(e) =>
                onDisplayPreferencesChange({
                  ...displayPreferences,
                  showBorder: e.target.checked,
                })
              }
            />
            <span>Show bounding box border</span>
          </label>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showFill}
              onChange={(e) =>
                onDisplayPreferencesChange({
                  ...displayPreferences,
                  showFill: e.target.checked,
                })
              }
            />
            <span>Show bounding box fill</span>
          </label>
        </div>
      )}
      <div className={styles.filters}>
        <label className={styles.filter}>
          <span>Class</span>
          <select
            value={classFilter}
            onChange={(e) => setClassFilter(e.target.value)}
          >
            {classOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt === "all" ? "All classes" : opt}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.filter}>
          <span>Sort by</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="none">None</option>
            <option value="type">Type</option>
            <option value="confidence">Confidence</option>
          </select>
        </label>
        {(onShowAll || onHideAll || onExport) && (
          <div className={styles.actions}>
            {!allVisible && onShowAll && (
              <button
                className={styles.actionButton}
                onClick={onShowAll}
                type="button"
              >
                Show All
              </button>
            )}
            {!allHidden && onHideAll && (
              <button
                className={styles.actionButton}
                onClick={onHideAll}
                type="button"
              >
                Hide All
              </button>
            )}
            {onExport && items.length > 0 && (
              <button
                className={styles.actionButton}
                onClick={onExport}
                type="button"
              >
                Export CSV
              </button>
            )}
          </div>
        )}
      </div>
      <div className={styles.list}>
        {filteredItems.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📊</div>
            <div className={styles.emptyStateTitle}>No results found</div>
            <div className={styles.emptyStateMessage}>
              {items.length === 0
                ? "Upload an image and click 'Analyze' to detect dental pathologies."
                : "No results match your current filters. Try adjusting the filters above."}
            </div>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div
              key={item.id || item.label}
              className={`${styles.item} ${item.hidden ? styles.hidden : ""}`}
              style={{
                borderLeftColor: item.color || undefined,
              }}
              onClick={() => onToggleBox && onToggleBox(item.id)}
              onMouseEnter={() => onHoverItem && onHoverItem(item.id)}
              onMouseLeave={() => onHoverItem && onHoverItem(null)}
            >
              <div className={styles.content}>
                <div className={styles.headerRow}>
                  <span className={styles.title}>{item.label}</span>
                  {item.meta && (
                    <span
                      className={styles.confidenceBadge}
                      style={{
                        backgroundColor: item.color || "#323232",
                        color: getTextColorForBackground(item.color || "#323232"),
                      }}
                    >
                      {item.meta}
                    </span>
                  )}
                </div>
                <div className={styles.detailsRow}>
                  <span
                    className={styles.icon}
                    style={{ color: item.color || "inherit" }}
                  >
                    <FaTooth />
                  </span>
                  {item.description && (
                    <span className={styles.description}>
                      {item.description}
                    </span>
                  )}
                </div>
                {onToggleBox && (
                  <div className={styles.toggle}>
                    {item.hidden ? "Show" : "Hide"}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

export default Results;
